import { SOCIAL_COMPOSE_PRODUCT_CHROME as composeChrome } from '../social-post/socialActionPresentation';
import {
  buildMediaWritePayload,
  hasPendingMediaUpload,
  mediaFingerprint,
} from '../social-post/socialPostMediaModel';

export const SOCIAL_COMPOSE_KINDS = Object.freeze(['MOOD', 'FOOD']);
/** 打卡專用 compose（PD-CHK-01）；不併入一般貼文 kind 切換。 */
export const SOCIAL_CHECKIN_KIND = 'PLACE';
export const SOCIAL_COMPOSE_VISIBILITIES = Object.freeze(['PUBLIC', 'FOLLOWERS', 'PRIVATE']);
export const SOCIAL_COMPOSE_REPLY_POLICIES = Object.freeze(['EVERYONE', 'FOLLOWERS', 'NONE']);
export const SOCIAL_COMPOSE_QUOTE_POLICIES = Object.freeze(['ALLOW', 'DISALLOW']);
export const SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY = 'EVERYONE';
export const SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY = 'ALLOW';
export const SOCIAL_COMPOSE_KIND_LABELS = Object.freeze({
  MOOD: '心情',
  FOOD: '美食',
  PLACE: '打卡',
});
export const SOCIAL_COMPOSE_VISIBILITY_LABELS = Object.freeze({
  PUBLIC: '公開',
  FOLLOWERS: '追蹤者',
  PRIVATE: '僅自己',
});
export const SOCIAL_COMPOSE_REPLY_POLICY_LABELS = Object.freeze({
  EVERYONE: '所有人',
  FOLLOWERS: '追蹤者可回覆',
  NONE: '禁止回覆',
});
export const SOCIAL_COMPOSE_QUOTE_POLICY_LABELS = Object.freeze({
  ALLOW: '允許引用',
  DISALLOW: '禁止引用',
});
export const SOCIAL_TEXT_MAX = 4000;
export const SOCIAL_COVER_REF_MAX = 512;
export const SOCIAL_CLIENT_REQUEST_ID_MAX = 64;
export const SOCIAL_MEDIA_WRITE_PREFIX = 'travel/image/';

export function normalizeReplyPolicy(value) {
  const policy = String(value || '').trim();
  return SOCIAL_COMPOSE_REPLY_POLICIES.includes(policy)
    ? policy
    : SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY;
}

export function normalizeQuotePolicy(value) {
  const policy = String(value || '').trim();
  return SOCIAL_COMPOSE_QUOTE_POLICIES.includes(policy)
    ? policy
    : SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY;
}

export function createSocialComposeClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `travel-web-${Date.now()}-${Math.random().toString(36).slice(2)}`.slice(0, SOCIAL_CLIENT_REQUEST_ID_MAX);
}

export function snowflakePositiveId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

export function isTravelImageObjectKey(value) {
  const key = String(value || '').trim();
  if (!key || key.length > SOCIAL_COVER_REF_MAX) return false;
  if (key.includes('..') || key.includes('\\') || /https?:\/\//i.test(key)) return false;
  return key.startsWith(SOCIAL_MEDIA_WRITE_PREFIX);
}

export function locationPathLabel(node, ancestors = []) {
  const names = [...ancestors, node]
    .map((item) => String(item?.displayName || item?.canonicalName || '').trim())
    .filter(Boolean);
  return names.join(' / ');
}

export function normalizeGeoLocationNode(raw) {
  const locationId = snowflakePositiveId(raw?.locationId);
  if (!locationId) return null;
  return {
    locationId,
    parentLocationId: snowflakePositiveId(raw?.parentLocationId),
    locationType: String(raw?.locationType || ''),
    countryCode: String(raw?.countryCode || ''),
    canonicalName: String(raw?.canonicalName || ''),
    displayName: String(raw?.displayName || raw?.canonicalName || 'Location'),
    hasChildren: Boolean(raw?.hasChildren),
  };
}

export function normalizeGeoLocationList(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map(normalizeGeoLocationNode).filter(Boolean);
}

export function validateSocialComposeDraft(draft) {
  const errors = {};
  const kind = String(draft?.postKind || '');
  const visibility = String(draft?.visibility || '');
  const replyPolicy = String(draft?.replyPolicy || '');
  const quotePolicy = String(draft?.quotePolicy || '');
  const text = String(draft?.textContent || '');
  const allowedKinds = draft?.allowCheckInKind
    ? [...SOCIAL_COMPOSE_KINDS, SOCIAL_CHECKIN_KIND]
    : SOCIAL_COMPOSE_KINDS;
  if (!allowedKinds.includes(kind)) {
    errors.postKind = composeChrome.validation.postKind;
  }
  if (!SOCIAL_COMPOSE_VISIBILITIES.includes(visibility)) {
    errors.visibility = composeChrome.validation.visibility;
  }
  if (!SOCIAL_COMPOSE_REPLY_POLICIES.includes(replyPolicy)) {
    errors.replyPolicy = composeChrome.validation.replyPolicy;
  }
  if (!SOCIAL_COMPOSE_QUOTE_POLICIES.includes(quotePolicy)) {
    errors.quotePolicy = composeChrome.validation.quotePolicy;
  }
  if (text.length > SOCIAL_TEXT_MAX) {
    errors.textContent = composeChrome.validation.textTooLong(SOCIAL_TEXT_MAX);
  }
  const trimmed = text.trim();
  const locationId = snowflakePositiveId(draft?.linkedLocationId);
  const journeyPublicationId = snowflakePositiveId(draft?.linkedJourneyPublicationId);
  if (kind === 'MOOD' && !trimmed && !journeyPublicationId) {
    errors.textContent = composeChrome.validation.moodNeedsText;
  }
  if (kind === 'FOOD' && !trimmed && !locationId && !journeyPublicationId) {
    errors.textContent = composeChrome.validation.foodNeedsTextOrLocation;
  }
  if (kind === SOCIAL_CHECKIN_KIND && !locationId) {
    errors.linkedLocationId = composeChrome.validation.checkInNeedsLocation;
  }
  const mediaItems = Array.isArray(draft?.mediaItems) ? draft.mediaItems : [];
  if (hasPendingMediaUpload(mediaItems) || mediaItems.some((item) => item.uploadState === 'uploading' || item.uploadState === 'failed')) {
    errors.media = '請先完成圖片上傳，或移除失敗的圖片。';
  } else if (mediaItems.some((item) => item.fileReference && !isTravelImageObjectKey(item.fileReference))) {
    errors.media = composeChrome.validation.coverKeyInvalid;
  }
  return errors;
}

/**
 * Standalone Social create body. Omits Memory origin and author.
 * PD-ATT-01：MOOD／FOOD 可選附 `linkedJourneyPublicationId`（不占 media）。
 * PD-CHK-01：PLACE 必帶 `linkedLocationId`。
 * quotedSocialPostId is included only when draft carries a positive id (quote compose).
 * linkedLocationId is sent for FOOD（可選）與 PLACE（必填）。
 */
export function buildCreateSocialPostBody(draft, { clientRequestId } = {}) {
  const requestId = String(clientRequestId || '').trim();
  const body = {
    postKind: String(draft?.postKind || ''),
    visibility: String(draft?.visibility || ''),
    replyPolicy: normalizeReplyPolicy(draft?.replyPolicy),
    quotePolicy: normalizeQuotePolicy(draft?.quotePolicy),
    clientRequestId: requestId,
  };
  const text = String(draft?.textContent || '').trim();
  if (text) body.textContent = text;
  body.media = buildMediaWritePayload(draft?.mediaItems);
  if (body.postKind === 'FOOD' || body.postKind === SOCIAL_CHECKIN_KIND) {
    const locationId = snowflakePositiveId(draft?.linkedLocationId);
    if (locationId) body.linkedLocationId = locationId;
  }
  const linkedJourneyPublicationId = snowflakePositiveId(draft?.linkedJourneyPublicationId);
  if (linkedJourneyPublicationId
      && (body.postKind === 'MOOD' || body.postKind === 'FOOD')) {
    body.linkedJourneyPublicationId = linkedJourneyPublicationId;
  }
  const quotedSocialPostId = snowflakePositiveId(draft?.quotedSocialPostId);
  if (quotedSocialPostId) body.quotedSocialPostId = quotedSocialPostId;
  return body;
}

export function createdSocialPostId(result) {
  return snowflakePositiveId(result?.postId);
}

/**
 * Text-only edits omit media so the backend keeps the derived cover.
 * Media changes send a full ordered replacement.
 * Policies are sent only when changed; omit keeps backend preserve semantics.
 * Unchanged drafts return null.
 */
export function buildEditSocialPostBody({
  textContent,
  mediaItems,
  expectedRowVersion,
  baselineText = '',
  baselineMedia = '',
  replyPolicy,
  quotePolicy,
  baselineReplyPolicy = SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY,
  baselineQuotePolicy = SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY,
} = {}) {
  const text = String(textContent || '').trim();
  const media = mediaFingerprint(mediaItems);
  const nextReply = normalizeReplyPolicy(replyPolicy);
  const nextQuote = normalizeQuotePolicy(quotePolicy);
  const baselineReply = normalizeReplyPolicy(baselineReplyPolicy);
  const baselineQuote = normalizeQuotePolicy(baselineQuotePolicy);
  const textChanged = text !== String(baselineText || '').trim();
  const mediaChanged = media !== String(baselineMedia || '');
  const policyChanged = nextReply !== baselineReply || nextQuote !== baselineQuote;
  if (!textChanged && !mediaChanged && !policyChanged) return null;
  const body = { expectedRowVersion, textContent: text };
  if (mediaChanged) body.media = buildMediaWritePayload(mediaItems);
  if (policyChanged) {
    body.replyPolicy = nextReply;
    body.quotePolicy = nextQuote;
  }
  return body;
}
